<template>
  <template v-if="!isLoading">
    <div class="rounded-lg shadow-md border-l border-r border-t border-grey-light-200 mt-0 bg-white">
      <div class="p-4 flex flex-col md:flex-row gap-4">
        <Button @click="onGoBack" variant="outline">
          <ArrowLeft class="h-4 w-4" />
        </Button>
        <div>
          <h2 class="text-xl text-black font-semibold">Settlement Report</h2>
          <p class="text-grey-light-600">Reference: {{ settlementDetails?.reference }}</p>
          <p v-if="downloadErr !== ''" class="text-sm font-medium text-destructive">{{ downloadErr }}</p>
        </div>
        <div class="flex gap-2 md:ml-auto">
          <Button @click="downloadFile" variant="outline" :disabled="downloading">
            <Download v-if="!downloading" class="h-4 w-4" />
            <LayupSpinner v-else :siz="4" />
          </Button>
        </div>
      </div>
      <div class="border-t border-grey-light-200">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3 p-4">
          <div class="col-span-1">
            <p class="text-grey-light-600 text-sm font-medium">Transaction Amount</p>
            <p class="text-brand-700 text-xl font-semibold">
              {{ currency(settlementDetails?.feesPerPaymentOnlineSettlementAmount) }}
            </p>
          </div>

          <div class="col-span-1">
            <p class="text-grey-light-600 text-sm font-medium">Fees (incl VAT)</p>
            <p class="text-brand-700 text-xl font-semibold">{{ calculateFees() }}</p>
          </div>
          <div class="col-span-1">
            <p class="text-grey-light-600 text-sm font-medium">Settlement Amount</p>
            <p class="text-brand-700 text-xl font-extrabold">{{ currency(settlementDetails?.amount) }}</p>
          </div>
        </div>
      </div>
    </div>
    <div class="mt-6 overflow-y-auto">
      <div class="flex">
        <Button
          v-for="tab in tabs"
          :key="tab.value"
          @click="onUpdateTab(tab.value)"
          type="button"
          variant="outline"
          class="flex items-center gap-2 md:min-w-[140px]"
          :class="[{ 'text-grey-light-800 ': selectedTab === tab.value }, tab.class]"
        >
          <div class="h-2 w-2 rounded-full bg-success-500" v-if="selectedTab === tab.value"></div>
          {{ tab.name }}
        </Button>
      </div>
    </div>
    <div class="mt-6">
      <SummaryTab v-if="selectedTab === 'summary'" :settlement-details="settlementDetails" @download="downloadFile" />
      <OrdersTab v-if="selectedTab === 'orders'" :settlement-details="settlementDetails" />
      <TransactionsTab v-if="selectedTab === 'transactions'" :settlement-details="settlementDetails" />
    </div>
  </template>
  <div class="flex justify-center">
    <LayupSpinner :size="10" v-if="isLoading" />
  </div>
</template>

<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import { Button } from '@/components/ui/button';
import { NetworkError } from '@/errors';
import type { Settlement } from '@/models/settlements';
import { fetchSettlementById, fetchSettlementFileLink } from '@/services/settlements';
import { currency } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { delay } from '@/utils/time.utils';
import { ArrowLeft, Download } from 'lucide-vue-next';
import { onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { OrdersTab, SummaryTab, TransactionsTab } from './tabs';

const router = useRouter();
const route = useRoute();
const id = route.params.id as string;

const selectedTab = ref('summary');
const isLoading = ref(true);
const settlementDetails = ref<Settlement>();
const downloading = ref(false);
const downloadErr = ref('');

const tabs = [
  { name: 'Summary', value: 'summary', class: 'rounded-r-none' },
  { name: 'Orders', value: 'orders', class: 'rounded-none' },
  { name: 'Transactions', value: 'transactions', class: 'rounded-l-none' },
];

onMounted(async () => {
  await loadSettlement();
});

watch(
  () => route.params.id,
  async () => {
    await loadSettlement();
  },
);

const onUpdateTab = (tab: string) => {
  selectedTab.value = tab;
};

const loadSettlement = async () => {
  try {
    isLoading.value = true;
    const settlementResponse = await fetchSettlementById(id);
    settlementDetails.value = settlementResponse ?? null;
    addMetric('settlement-details', { status: 'success', channel: 'merchant', method: 'fetchSettlementById' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('settlement-details', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('settlement-details', { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

const calculateFees = () => {
  if (settlementDetails.value) {
    const feesExVat = settlementDetails.value.feesPerProductExcludingVAT ?? 0;
    const feesVat = settlementDetails.value.feesPerProductVAT ?? 0;
    return currency(feesExVat + feesVat);
  }
  return 'No fees set';
};

const downloadFile = async () => {
  try {
    // A settlement should always have a zip file available for download.
    if (!settlementDetails.value?.reference) {
      throw new Error('Something went wrong, no document available for download.');
    }
    downloading.value = true;
    downloadErr.value = '';
    const link = document.createElement('a');
    link.href = await fetchSettlementFileLink(`${settlementDetails.value.reference}.zip`);
    link.setAttribute('download', `Settlement Report - ${settlementDetails.value?.reference}.zip`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Delay to keep spinner visible for a brief moment to indicate download started
    await delay(500);
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('settlement-detail', { status: 'network_error', channel: 'merchant', method: 'downloadDocument' });
      downloadErr.value = `Network error, request could not be completed.`;
    } else if (error instanceof Error) {
      createException(error);
      addMetric('settlement-detail', { status: 'error', channel: 'merchant', method: 'downloadDocument' });
      downloadErr.value = `${error.message}`;
    }
  } finally {
    downloading.value = false;
  }
};

const onGoBack = () => {
  router.push({ name: 'merchantSettlements' });
};
</script>
